import HttpHelper from '@/helpers/http';
import { IUser, IUserFlag, IUserStreamStatus, IIceConfig, IUserSettings } from '@/helpers/user';
import { ISubscription } from '@/helpers/subscription';
import { EEditorType } from '@/helpers/editor';
import { TDestinationType } from '@/components/account/PlatformSettings.vue';
import { IStreamInfo } from '@/helpers/platforms';
import { getCachedValue } from '@/helpers/cache';

interface ISubscriptionResponse {
  success: boolean;
  subscription?: ISubscription;
  message?: string;
}
export default class UserApi {
  static async getMe(): Promise<IUser> {
    return await HttpHelper.authGet('/api/v1/users/me', { background: true });
  }

  static async getStreamStatus(): Promise<IUserStreamStatus> {
    return await HttpHelper.authGet(`/api/v1/users/me/streams/status`, { background: true });
  }

  static async getUserFlags(): Promise<{ flags: IUserFlag; }> {
    return await HttpHelper.authGet('/api/v1/users/me/flags', { background: true });
  }

  static async getUserSubscription(): Promise<ISubscriptionResponse> {
    return await HttpHelper.authGet('/api/v1/users/subscriptions/current', { background: true });
  }

  static async getStreamlabsAuthUrl() {
    return await HttpHelper.get('/auth/streamlabs');
  }

  static async disconnectStreamlabsUser() {
    return await HttpHelper.delete('/api/v1/users/me/streamlabs/disconnect');
  }

  static async disconnectStreamlabsIdentity() {
    return await HttpHelper.delete('/api/v1/users/me/streamlabs-identity/disconnect');
  }

  static async getUserWidgetTokenByIdentifier(identifier: string, sessionId: string = null) {
    return await HttpHelper.authGet(`/api/v1/users/${identifier}/widgetToken${sessionId ? `?session_id=${sessionId}` : ''}`, { background: true });
  }

  static async setUserEditorOnboardingShown() {
    return await HttpHelper.post('/api/v1/users/me/flags/editor-onboarding-completed', null, { background: true });
  }

  static async setUserProjectOnboardingShown() {
    return await HttpHelper.post('/api/v1/users/me/flags/project-onboarding-completed', null, { background: true });
  }

  static async removeFirstSuccessfulTransactionFlag() {
    return await HttpHelper.delete('/api/v1/users/me/flags/first-successful-transaction', { background: true });
  }

  static async removeStreamAttemptFlag() {
    return await HttpHelper.delete('/api/v1/users/me/flags/stream-attempt', { background: true });
  }

  static async removeShowOnboardingFlag() {
    return await HttpHelper.delete('/api/v1/users/me/flags/show-onboarding', { background: true });
  }

  static async setTransitionTooltipShown() {
    return await HttpHelper.post('/api/v1/users/me/flags/transition-tooltip-shown', null, { background: true });
  }

  static async setEditStreamInfoButtonDismissed() {
    return await HttpHelper.post('/api/v1/users/me/flags/edit-stream-info-button-dismissed', null, { background: true });
  }

  static async getCsrfToken(): Promise<IUser> {
    return await HttpHelper.authGet('/api/v1/users/me/csrf-token', { background: true });
  }

  static async getIceConfig(identifier: string, sessionId: string = null): Promise<{ config: IIceConfig; }> {
    return getCachedValue('user-ice-config', async () => {
      const response = await HttpHelper.get(`/api/v1/users/${identifier}/ice/config${sessionId ? `?session_id=${sessionId}` : ''}`, { background: true, ignoreError: true });
      return response;
    });
  }

  static async updateUserConsents(type: string, version?: string) {
    return await HttpHelper.post('/api/v1/users/me/consents', {
      type,
      version,
    });
  }

  static async saveUserSettings(settings: IUserSettings) {
    return await HttpHelper.post('/api/v1/users/me/settings', {
      settings,
    });
  }

  static async unlinkAccount(account: string) {
    return await HttpHelper.post(`/api/v1/accounts/unlink/${account}`);
  }

  static async setStreamDestination(account: string) {
    return await HttpHelper.post(`/api/v1/accounts/destination/${account}`);
  }

  static async setPlatformOrientation(account: TDestinationType, orientation: EEditorType.horizontal | EEditorType.vertical) {
    return await HttpHelper.post(`/api/v1/accounts/orientation/${account}`, {
      orientation
    }, {
      background: true,
    });
  }

  static async getYoutubeLiveChatId() {
    return await HttpHelper.get('/api/v1/accounts/youtube/videoId', { background: true });
  }

  static async updateStreamInfo(streamInfo: IStreamInfo) {
    return await HttpHelper.post('/api/v1/users/me/stream-info', {
      streamInfo,
    });
  }
}
